/**
 * Swimlane (flow / sequence) composer — pure drawing off the P3a
 * `swimlaneGeometry` pass + the shared `swimlaneTopology`. Ports the committed
 * SwimlaneView's SVG overlay (git show HEAD:packages/app-shell/src/process/
 * SwimlaneView.tsx) coordinate-for-coordinate: blocks, lane chrome, frames +
 * UML-alt bands + branch-tab chips, gateway diamonds + leg buses/gutters, the
 * crossing-minimised flow bridges (optimizeBridges), aux back-edges (saga
 * drops / reverse compensation chain / `go` re-entries), sequence lifelines +
 * arrows + re-entry arcs, and the comment-label band. Committed quirks are
 * preserved, not improved.
 *
 * Walk-derived signals the layout rows don't carry enter via options —
 * `auxEdges` (row-index endpoints), `stepFlags` (compensation/dist),
 * `isNoteRow`, `triggerRowIndexes`, `entryStepIndex` — resolved by the caller
 * (headless adapters map them from the engine walk; the live view already
 * holds them).
 *
 * Interactivity consciously not composed (live-shell overlays): step-travel
 * droplets, hover states, pick cursors/hit-targets, the seq band's
 * "click to expand" hint (the uppercase "subprocess" tag stays).
 *
 * Deliberate approximations: the block ▶ trigger glyph, `do` caret, and
 * their labels sit at fixed offsets instead of the browser's inline-flex
 * centring (the measure is used only for the terminate-all chip and the
 * right-edge overhang sizing).
 */

import { metricsMeasure } from "../metrics.js";
import type { Measure } from "../text.js";
import type { BpmnGroup, BpmnShape } from "./compose-bpmn.js";
import type { Frame, Row, SwimlaneSceneInput } from "./types.js";
import {
  swimlaneTopology,
  showsBranchLabels,
  isFrameExclusive,
  type SwimlaneGeometry,
  type SwimlaneGeometryOptions,
  type SwimlaneTopology,
} from "./swimlane-geometry.js";
import {
  blockBounds,
  commentLabel,
  roundedPolyPath,
} from "./swimlane-routing.js";
import {
  swimlaneSeqArcs,
  swimlaneRouting,
  swimlaneFlowEdgeGeom,
  swimlaneFlowAux,
  swimlaneGatewayGeom,
  seqSelfCallArc,
  type SwimlaneAuxEdge,
  type SwimlaneGwGeom,
} from "./swimlane-edges.js";
import {
  AUX_HEAD,
  BLOCK_BORDER,
  BLOCK_INSET,
  BLOCK_LINE_H,
  BLOCK_PAD_X,
  BLOCK_PAD_Y,
  BLOCK_RADIUS,
  BLOCK_SUB_LINE_H,
  BLOCK_SUB_MARGIN_TOP,
  BLOCK_TAG_MARGIN_BOTTOM,
  DO_FLOW_PAD_X,
  DO_FLOW_PAD_Y,
  FRAME_GAP,
  GW_HALF,
  GUTTER_BASE,
  GUTTER_TRACK,
  LANE_WIDTH,
  OVER_RISE,
  ROW_MIN_H,
  TERM_CHIP_BORDER,
  TERM_CHIP_FONT_SIZE,
  TERM_CHIP_GAP,
  TERM_CHIP_H,
  TERM_CHIP_PAD_X,
} from "./swimlane-metrics.js";
import { defaultProcessTokens, type ProcessTokens } from "./tokens.js";

const r2 = (n: number): number => Math.round(n * 100) / 100;
/** Width of the v0.10.2 latency ruler gutter (left of x=0, sequence export). */
const RULER_GUTTER_W = 60;

// ─────────────────────────────────────────────────────────────────────────────
// Options / output
// ─────────────────────────────────────────────────────────────────────────────

/** Walk-derived per-step styling the rows don't carry. */
export interface SwimlaneStepFlags {
  readonly compensation?: boolean;
  readonly dist?: boolean;
}

export interface ComposeSwimlaneOptions extends SwimlaneGeometryOptions {
  readonly tokens?: ProcessTokens;
  /** Text measure (default: the AFM `metricsMeasure`) — sizes the
   *  terminate-all chip and the right-edge label overhang. Pass the SAME
   *  measure the geometry pass used or centring drifts. */
  readonly measure?: Measure;
  /** Saga drops / comp chain / `go` back-arcs (flow) and re-entry arcs (seq). */
  readonly auxEdges?: readonly SwimlaneAuxEdge[];
  /** Default: `compensation` from `row.comp` presence, `dist` false. */
  readonly stepFlags?: (row: Row, rowIndex: number) => SwimlaneStepFlags;
  /** A note block (`Module "text"` — noteText with a caller): amber styling,
   *  no sequence arrow. Default: never. */
  readonly isNoteRow?: (row: Row, rowIndex: number) => boolean;
  // `triggerRowIndexes` (START-trigger rows → ▶ prefix + data-trigger) is
  // inherited from SwimlaneGeometryOptions — the geometry pass budgets the
  // marker into line 0's wrap; the composer paints the glyph from the same set.
  /** stepIndex of the first REAL (non-comment) step — gets the entry dot.
   *  Default: the smallest stepIndex among non-comment step rows. */
  readonly entryStepIndex?: number;
  /** v0.10.2 per-hop latency label for a step's arrow (`200ms`, `???`), keyed
   *  by stepIndex — undefined = no label. Threaded from the engine's
   *  `perStepLatency`, so exported SVG/PNG matches the live per-hop labels. */
  readonly stepLatencyLabel?: (stepIndex: number) => string | undefined;
  /** v0.10.2 cumulative-elapsed label for a step (`~340ms`), keyed by stepIndex
   *  — the sequence-view ruler (sequence variant only). Presence adds a left
   *  gutter; the caller threads `perStepLatency`'s `cumLabel`. */
  readonly stepCumulativeLabel?: (stepIndex: number) => string | undefined;
}

export interface ComposedSwimlane {
  readonly width: number;
  readonly height: number;
  /** v0.10.2 latency-ruler gutter to the LEFT of x=0 (0 when no ruler). The SVG
   *  serializer extends the viewBox left by this so the ruler is visible. */
  readonly leftGutter: number;
  /** Lane headers + HTML-layer cells (blocks/do bands/terminators/prose). */
  readonly nodes: readonly BpmnGroup[];
  /** The SVG overlay, painted OVER the nodes in committed order. */
  readonly overlay: readonly BpmnGroup[];
  /** Which arrowhead markers the output references. */
  readonly markers: {
    readonly seq: boolean;
    readonly flowTick: boolean;
    readonly flowTickErr: boolean;
    readonly compTick: boolean;
    readonly reentryTick: boolean;
  };
}

type MarkerName = "seq" | "flowTick" | "flowTickErr" | "compTick" | "reentryTick";

// ─────────────────────────────────────────────────────────────────────────────
// The composer
// ─────────────────────────────────────────────────────────────────────────────

export function composeSwimlane(
  input: SwimlaneSceneInput,
  geom: SwimlaneGeometry,
  opts: ComposeSwimlaneOptions = {},
): ComposedSwimlane {
  const t = opts.tokens ?? defaultProcessTokens();
  const measure = opts.measure ?? metricsMeasure;
  const fontFamily = opts.fontFamily ?? t.fontFamily;
  // Scale-resolved metrics come from the geometry pass so the composed overlay
  // paints at the EXACT same `fontScale`/`layoutScale` the boxes were sized at.
  // These shadow the module's scale-1 imports; font sizes replace the former
  // literals; `fontScale` scales the text-baseline offsets (see `bl` below).
  const {
    fontScale,
    AUX_HEAD, BLOCK_BORDER, BLOCK_INSET, BLOCK_LINE_H, BLOCK_PAD_X, BLOCK_PAD_Y,
    BLOCK_RADIUS, BLOCK_SUB_LINE_H, BLOCK_SUB_MARGIN_TOP, BLOCK_TAG_MARGIN_BOTTOM,
    BOARD_RIGHT_PAD, DO_FLOW_PAD_X, DO_FLOW_PAD_Y, FRAME_GAP, GUTTER_BASE,
    GUTTER_TRACK, GW_HALF, LANE_WIDTH, OVER_RISE, ROW_MIN_H, TERM_CHIP_BORDER,
    TERM_CHIP_FONT_SIZE, TERM_CHIP_GAP, TERM_CHIP_H, TERM_CHIP_PAD_X,
    BLOCK_FONT_SIZE, BLOCK_SUB_FONT_SIZE, TERM_FONT_SIZE, LANE_HEADER_FONT_SIZE,
    FRAME_LABEL_FONT_SIZE, LABEL_FONT_SIZE, ANNOTATION_FONT_SIZE,
    DO_CARET_FONT_SIZE, DO_LABEL_FONT_SIZE, DO_HINT_FONT_SIZE,
    LANE_HEADER_CHIP_INSET_X, LANE_HEADER_CHIP_INSET_Y, LANE_HEADER_CHIP_W,
    LANE_HEADER_LINE_H,
  } = geom.metrics;
  // Text-baseline offset scaler: the `+N` ascent offsets in text y-positions
  // are proportional to font size, so they track `fontScale` (×1 at scale 1).
  const bl = (n: number): number => n * fontScale;
  const variant = input.variant;
  const nLanes = Math.max(input.lanes.length, 1);
  const topo = swimlaneTopology(input, opts);
  const { laneIndexOf: laneIndex, flowChain, gateways, gatewaySkip, hiddenRowIndexes } = topo;
  const used: Record<MarkerName, boolean> = { seq: false, flowTick: false, flowTickErr: false, compTick: false, reentryTick: false };
  const MARKER_NAME = {
    seq: "swArrow",
    flowTick: "swFlowTick",
    flowTickErr: "swFlowTickErr",
    compTick: "swCompTick",
    reentryTick: "swReentryTick",
  } as const;
  const mark = (m: MarkerName): (typeof MARKER_NAME)[MarkerName] => ((used[m] = true), MARKER_NAME[m]);

  const stepFlags = (r: Row, i: number): SwimlaneStepFlags =>
    opts.stepFlags?.(r, i) ?? { compensation: r.comp !== undefined };
  const isNote = (r: Row, i: number): boolean => opts.isNoteRow?.(r, i) ?? false;
  const triggers = opts.triggerRowIndexes ?? new Set<number>();
  const waits = opts.waitRowIndexes ?? new Set<number>();
  const entryStepIndex =
    opts.entryStepIndex ??
    (() => {
      let min: number | undefined;
      input.rows.forEach((r, i) => {
        if (r.stepIndex === null) return;
        if (opts.isCommentRow?.(r, i)) return;
        if (min === undefined || r.stepIndex < min) min = r.stepIndex;
      });
      return min;
    })();

  const rowTopPad = (y: number): number => geom.rowTopPads[y] ?? 8;
  const rowBottomPad = (y: number): number => geom.rowBottomPads[y] ?? 8;
  const bounds = (y: number, lane: number) => blockBounds(geom, y, lane);
  // Occupancy = EVERY row with a resolvable toLaneId — including gateway rows
  // (their toLaneId is the fork lane) and pick-hidden rows — the committed
  // rowOccupancy (SwimlaneView 274-293). Deriving this from rendered blocks
  // loses gateway rows and sends same-lane aux edges through the diamonds.
  const occupancy = (() => {
    const byRow = new Map<number, Set<number>>();
    for (const r of input.rows) {
      const li = laneIndex(r.toLaneId);
      if (li < 0) continue;
      let set = byRow.get(r.y);
      if (!set) byRow.set(r.y, (set = new Set()));
      set.add(li);
    }
    return byRow;
  })();
  const laneOccupied = (y: number, lane: number): boolean => occupancy.get(y)?.has(lane) ?? false;
  const gridBottom = geom.headerH + geom.gridHeight;

  const nodes: BpmnGroup[] = [];
  const overlay: BpmnGroup[] = [];
  const g = (
    into: BpmnGroup[],
    attrs: Record<string, string | number | undefined>,
    shapes: BpmnShape[],
    opacity?: number,
    title?: string,
  ): void => {
    into.push({ attrs, shapes, ...(opacity !== undefined ? { opacity } : {}), ...(title ? { title } : {}) });
  };

  // ── Lane headers ──────────────────────────────────────────────────────────
  for (const lane of geom.lanes) {
    const chipX = lane.x + LANE_HEADER_CHIP_INSET_X;
    const chipW = LANE_HEADER_CHIP_W;
    const chipY = LANE_HEADER_CHIP_INSET_Y;
    const chipH = geom.headerH - 2 * LANE_HEADER_CHIP_INSET_Y;
    // One text per wrapped line (the geometry pass did the wrapping), stacked
    // centred in the chip — a long module name reads on two lines instead of
    // spilling across its neighbours.
    const lines = lane.labelLines.length ? lane.labelLines : [lane.label];
    const firstBaseline =
      chipY + chipH / 2 - ((lines.length - 1) * LANE_HEADER_LINE_H) / 2 + bl(4);
    g(nodes, { "data-lane-header": lane.index }, [
      { kind: "rect", x: chipX, y: chipY, w: chipW, h: chipH, rx: 6, fill: t.laneHeaderFill, stroke: t.laneHeaderStroke, strokeWidth: 1 },
      ...lines.map((text, i): BpmnShape => ({
        kind: "text",
        x: lane.cx,
        y: firstBaseline + i * LANE_HEADER_LINE_H,
        text,
        fill: t.laneHeaderText,
        size: LANE_HEADER_FONT_SIZE,
        anchor: "middle",
      })),
    // A wrapped/ellipsized chip carries the full name as a tooltip; a label
    // that fits verbatim needs none.
    ], undefined, lines.join("") === lane.label ? undefined : lane.label);
  }

  // ── Blocks / cells (the HTML grid layer) ──────────────────────────────────
  for (const b of geom.blockList) {
    const row = input.rows[b.rowIndex]!;
    const flags = stepFlags(row, b.rowIndex);
    const dimmed = b.dimmed;
    const { rect } = b;
    const attrs: Record<string, string | number | undefined> = {
      "data-step-index": row.stepIndex ?? undefined,
      "data-row-y": row.y,
      "data-lane": b.laneIndex >= 0 ? b.laneIndex : undefined,
    };

    if (b.kind === "terminator") {
      const term = row.terminator!;
      const isFail = term.type === "fail";
      const color = isFail ? t.terminatorFail : t.terminatorFinish;
      const all = term.all === true;
      const shapes: BpmnShape[] = [];
      if (all) {
        // BPMN terminate-style double ring (committed boxShadow 2px panel +
        // 3.5px colour → an outer colour ring 3px out, the canvas gap between).
        shapes.push({ kind: "rect", x: rect.x - 3, y: rect.y - 3, w: rect.w + 6, h: rect.h + 6, rx: (rect.h + 6) / 2, fill: "none", stroke: color, strokeWidth: 1.5 });
      }
      shapes.push({ kind: "rect", x: rect.x, y: rect.y, w: rect.w, h: rect.h, rx: rect.h / 2, fill: color });
      if (all) {
        // Committed in-pill "all" badge (2193-2201): reason + gap-1.5 + an
        // uppercase 8px bordered chip, the pair centred as one flex content.
        const reasonW = b.lines[0] ? measure.text(b.lines[0], b.font).width : 0;
        const chipFont = { family: b.font.family, size: TERM_CHIP_FONT_SIZE, weight: 400 };
        const chipW = 2 * (TERM_CHIP_PAD_X + TERM_CHIP_BORDER) + measure.text("ALL", chipFont).width;
        const left = b.cx - (reasonW + TERM_CHIP_GAP + chipW) / 2;
        if (b.lines[0]) {
          shapes.push({ kind: "text", x: left + reasonW / 2, y: b.cy + bl(3.5), text: b.lines[0], fill: t.terminatorText, size: BLOCK_FONT_SIZE, anchor: "middle" });
        }
        const chipX = left + reasonW + TERM_CHIP_GAP;
        shapes.push({
          kind: "rect", x: chipX, y: b.cy - TERM_CHIP_H / 2, w: chipW, h: TERM_CHIP_H, rx: 4,
          fill: "none", stroke: "rgba(255,255,255,0.6)", strokeWidth: TERM_CHIP_BORDER,
        });
        shapes.push({
          kind: "text", x: chipX + chipW / 2, y: b.cy + 3, text: "ALL",
          fill: t.terminatorText, size: TERM_CHIP_FONT_SIZE, anchor: "middle",
        });
      } else if (b.lines[0]) {
        shapes.push({ kind: "text", x: b.cx, y: b.cy + bl(3.5), text: b.lines[0], fill: t.terminatorText, size: BLOCK_FONT_SIZE, anchor: "middle" });
      }
      g(nodes, { ...attrs, "data-terminator": term.type, ...(all ? { "data-terminator-all": "true" } : {}) }, shapes, dimmed ? t.dimOpacity : undefined);
      continue;
    }

    if (b.kind === "do") {
      const sh = row.subprocessHeader!;
      const shapes: BpmnShape[] = [];
      // ▼/▶ (U+25BC/U+25B6), not the live ▾/▸: the bundled export Inter's cmap
      // has no U+25BE/U+25B8 (rasterizeSvg loads ONLY those TTFs) — the small
      // variants tofu in every PNG. Size 8 approximates the small glyphs.
      const caret = sh.expanded ? "▼" : "▶";
      if (variant === "flow") {
        shapes.push({ kind: "rect", x: rect.x, y: rect.y, w: rect.w, h: rect.h, rx: BLOCK_RADIUS, fill: t.subprocessFillFlow, stroke: t.subprocessAccent, strokeWidth: 1, dash: "4 3" });
        shapes.push({ kind: "text", x: rect.x + BLOCK_BORDER + DO_FLOW_PAD_X, y: b.cy + bl(3.5), text: caret, fill: t.subprocessCaret, size: ANNOTATION_FONT_SIZE });
        const top = rect.y + BLOCK_BORDER + DO_FLOW_PAD_Y;
        b.lines.forEach((line, i) => {
          shapes.push({ kind: "text", x: b.cx + 4, y: top + i * BLOCK_LINE_H + bl(10.5), text: line, fill: t.subprocessText, size: BLOCK_FONT_SIZE, anchor: "middle" });
        });
      } else {
        shapes.push({ kind: "rect", x: rect.x, y: rect.y, w: rect.w, h: rect.h, rx: BLOCK_RADIUS, fill: t.subprocessFillSeq, stroke: t.subprocessAccent, strokeWidth: 1 });
        shapes.push({ kind: "text", x: rect.x + 12, y: rect.y + bl(16), text: caret, fill: t.subprocessCaret, size: ANNOTATION_FONT_SIZE });
        shapes.push({ kind: "text", x: rect.x + 24, y: rect.y + bl(16), text: b.lines[0] ?? "do", fill: t.subprocessText, size: BLOCK_FONT_SIZE, weight: 500 });
        // The live band's right side reads "subprocess — click to …" — the
        // pointer hint is meaningless headless; the uppercase tag stays.
        shapes.push({ kind: "text", x: rect.x + rect.w - 12, y: rect.y + bl(16), text: "SUBPROCESS", fill: t.subprocessHint, size: LABEL_FONT_SIZE, anchor: "end" });
      }
      g(nodes, { ...attrs, "data-subprocess-do": sh.nodeId, "data-subprocess-expanded": sh.expanded ? "true" : "false" }, shapes, dimmed ? t.dimOpacity : undefined);
      continue;
    }

    if (b.kind === "comment" || b.kind === "prose") {
      const shapes: BpmnShape[] = [];
      b.lines.forEach((line, i) => {
        shapes.push({ kind: "text", x: b.cx, y: rect.y + i * BLOCK_LINE_H + bl(10.5), text: line, fill: t.blockSubtitle, size: BLOCK_FONT_SIZE, anchor: "middle", italic: true });
      });
      // Committed: inner 0.85 always, outer band fade on top when dimmed.
      g(nodes, { ...attrs, "data-comment": "true", ...(triggers.has(b.rowIndex) ? { "data-trigger": "true" } : {}) }, shapes, 0.85 * (dimmed ? t.dimOpacity : 1));
      continue;
    }

    // Step block.
    const note = isNote(row, b.rowIndex);
    const comp = flags.compensation === true && !note;
    const borderColor = note ? t.noteStroke : comp ? t.compStroke : t.blockStroke;
    const bg = note ? t.noteFill : comp ? t.compFill : t.blockFill;
    const labelColor = row.reentry ? t.reentryArc : comp ? t.compText : t.blockText;
    const subtitleColor = comp ? t.compSubtitle : t.blockSubtitle;
    const shapes: BpmnShape[] = [
      { kind: "rect", x: rect.x, y: rect.y, w: rect.w, h: rect.h, rx: BLOCK_RADIUS, fill: bg, stroke: borderColor, strokeWidth: 1, ...(note ? { dash: "4 3" } : {}) },
    ];
    let top = rect.y + BLOCK_BORDER + BLOCK_PAD_Y;
    if (b.branchTag) {
      shapes.push({ kind: "text", x: b.cx, y: top + bl(8.5), text: b.branchTag, fill: subtitleColor, size: LABEL_FONT_SIZE, anchor: "middle", italic: true });
      top += BLOCK_SUB_LINE_H + BLOCK_TAG_MARGIN_BOTTOM;
    }
    if (triggers.has(b.rowIndex)) {
      shapes.push({ kind: "text", x: rect.x + BLOCK_PAD_X + 2, y: top + bl(10.5), text: "▶", fill: labelColor, size: LABEL_FONT_SIZE });
    } else if (waits.has(b.rowIndex)) {
      // Mid-flow `await` — a drawn clock (circle + hand), cmap-safe like BPMN's
      // timer event, distinguishing a wait from a plain note.
      const cxg = rect.x + BLOCK_PAD_X + 2 + 3.5;
      const cyg = top + 5.5;
      shapes.push(
        { kind: "circle", cx: cxg, cy: cyg, r: 3.5, fill: "none", stroke: labelColor, strokeWidth: 1 },
        { kind: "path", d: `M ${cxg} ${cyg - 2} V ${cyg} L ${cxg + 1.6} ${cyg + 0.9}`, fill: "none", stroke: labelColor, strokeWidth: 1 },
      );
    }
    b.lines.forEach((line, i) => {
      shapes.push({ kind: "text", x: b.cx, y: top + i * BLOCK_LINE_H + bl(10.5), text: line, fill: labelColor, size: BLOCK_FONT_SIZE, anchor: "middle" });
    });
    if (b.subtitleLines.length) {
      const subTop = top + b.lines.length * BLOCK_LINE_H + BLOCK_SUB_MARGIN_TOP;
      b.subtitleLines.forEach((line, i) => {
        shapes.push({ kind: "text", x: b.cx, y: subTop + i * BLOCK_SUB_LINE_H + bl(8.5), text: line, fill: subtitleColor, size: LABEL_FONT_SIZE, anchor: "middle" });
      });
    }
    g(
      nodes,
      {
        ...attrs,
        ...(comp ? { "data-compensation-node": "true" } : {}),
        ...(row.reentry ? { "data-reentry": "true" } : {}),
        ...(triggers.has(b.rowIndex) ? { "data-trigger": "true" } : {}),
        ...(waits.has(b.rowIndex) ? { "data-wait": "true" } : {}),
      },
      shapes,
      dimmed ? t.dimOpacity : undefined,
    );
  }

  // ── Overlay: lane separators ──────────────────────────────────────────────
  {
    const shapes: BpmnShape[] = [];
    for (let i = 1; i < nLanes; i++) {
      shapes.push({ kind: "line", x1: i * LANE_WIDTH, y1: geom.headerH, x2: i * LANE_WIDTH, y2: gridBottom, stroke: t.laneSeparator, strokeWidth: 1, dash: "3 4" });
    }
    if (shapes.length) g(overlay, { "data-lane-separators": "true" }, shapes);
  }

  // ── Overlay: frames (sequence) / rails (flow) ─────────────────────────────
  const sortedFrames = [...geom.frames].sort((a, b) => {
    if (a.depth !== b.depth) return a.depth - b.depth;
    const fa = input.frames.find((f) => f.id === a.id)!;
    const fb = input.frames.find((f) => f.id === b.id)!;
    if (fa.yStart !== fb.yStart) return fa.yStart - fb.yStart;
    return a.id.localeCompare(b.id);
  });
  const branchSel = opts.branchSelection ?? new Map<string, string>();

  if (variant === "sequence") {
    for (const f of sortedFrames) {
      if (f.kind === "do") continue;
      const { x, y, w, h } = f.rect;
      const threshold = f.mergeAspect ?? (f.runCount !== undefined ? `≤${f.runCount} of ${(input.frames.find((ff) => ff.id === f.id)?.branches.length ?? 0)}` : undefined);
      const shapes: BpmnShape[] = [
        { kind: "rect", x, y, w, h, rx: 6, fill: t.seqFrameFill, stroke: t.seqFrameStroke, strokeWidth: 1 },
        { kind: "text", x: x + 8, y: y + bl(14), text: f.label, fill: t.seqFrameLabel, size: BLOCK_FONT_SIZE },
      ];
      if (threshold) {
        shapes.push({ kind: "text", x: x + w - 8, y: y + bl(14), text: threshold, fill: t.seqFrameLabel, size: LABEL_FONT_SIZE, weight: 600, anchor: "end" });
      }
      const picked = isFrameExclusive(f.kind) ? branchSel.get(f.id) : undefined;
      for (const band of f.branches) {
        if (!band.labelled) continue;
        if (picked !== undefined && band.label !== picked) continue;
        const lineY = band.topY + 2;
        shapes.push({ kind: "line", x1: x, y1: lineY, x2: x + w, y2: lineY, stroke: t.bandDivider, strokeWidth: 1, dash: "5 4" });
        if (showsBranchLabels(f.kind)) {
          shapes.push({
            kind: "text", x: x + 8, y: band.topY + 12, text: band.label || "(default)",
            fill: band.selected ? t.bandLabelSelected : t.bandLabelUnselected,
            size: LABEL_FONT_SIZE, italic: true, weight: band.selected ? 600 : 400,
          });
        }
      }
      // Branch tab chips (non-interactive port of BranchTabs).
      if (isFrameExclusive(f.kind)) {
        const frame = input.frames.find((ff) => ff.id === f.id)!;
        const labelGutter = 8 + f.label.length * 6 + 8;
        const maxX = x + w - (threshold ? 10 + threshold.length * 6 : 6);
        let cursor = x + labelGutter;
        const skipLabel = f.label.trim();
        for (const band of frame.branches) {
          if (band.label === skipLabel) continue;
          const text = band.label || "(default)";
          const tw = Math.min(text.length * 6 + 14, maxX - cursor);
          if (tw <= 12) break;
          const empty = band.yEnd < band.yStart;
          shapes.push({
            kind: "rect", x: cursor, y: y + 3, w: tw, h: 14, rx: 3,
            fill: band.selected ? t.tabFillSelected : t.tabFillUnselected,
            stroke: band.selected ? t.tabStrokeSelected : t.tabStrokeUnselected,
            strokeWidth: band.selected ? 1 : 0.6,
            ...(band.selected ? {} : { dash: "3 3" }),
          });
          shapes.push({
            kind: "text", x: cursor + 6, y: y + bl(14), text: text + (empty ? " ·" : ""),
            fill: band.selected ? t.tabTextSelected : empty ? t.tabTextEmpty : t.tabTextUnselected,
            size: LABEL_FONT_SIZE, weight: band.selected ? 600 : 400,
          });
          cursor += tw + FRAME_GAP;
          if (cursor >= maxX) break;
        }
      }
      g(overlay, { "data-frame-id": f.id }, shapes);
    }
    // Expanded `do` sections.
    for (const f of sortedFrames) {
      if (f.kind !== "do" || f.collapsed) continue;
      const frame = input.frames.find((ff) => ff.id === f.id)!;
      const insetY = f.depth * 3;
      const yStart = frame.yStart;
      const top = (geom.rowTops[yStart] ?? geom.headerH) + insetY + FRAME_GAP;
      const headerBottom = top + (geom.rowHeights[yStart] ?? ROW_MIN_H) - insetY - FRAME_GAP;
      const x = 8 + f.depth * 6;
      const w = nLanes * LANE_WIDTH - 16 - 2 * (f.depth * 6);
      let spanH = 0;
      const yEnd = Math.max(frame.yEnd, frame.yStart - 1);
      for (let yy = yStart; yy <= yEnd; yy++) spanH += geom.rowHeights[yy] ?? ROW_MIN_H;
      const h = Math.max(spanH - 2 * insetY - 2 * FRAME_GAP, 1);
      // Committed SubprocessSection strokeOpacity: rect 0.7, divider 0.4.
      g(overlay, { "data-subprocess-section": f.id }, [
        { kind: "rect", x, y: top, w, h, rx: 6, fill: t.subprocessSectionFill, stroke: t.subprocessAccent, strokeWidth: 1, strokeOpacity: 0.7 },
        { kind: "line", x1: x, y1: headerBottom, x2: x + w, y2: headerBottom, stroke: t.subprocessAccent, strokeWidth: 1, strokeOpacity: 0.4 },
      ]);
    }
  } else {
    // Flow: expanded-`do` left brackets + `each` loop rails.
    for (const f of sortedFrames) {
      const frame = input.frames.find((ff) => ff.id === f.id)!;
      const yStart = frame.yStart;
      const yEnd = Math.max(frame.yEnd, frame.yStart - 1);
      if (yEnd < yStart) continue;
      let minLane = Number.POSITIVE_INFINITY;
      for (const r of input.rows) {
        if (!r.enclosingFrames.includes(f.id)) continue;
        if (f.kind === "do" && r.subprocessHeader?.nodeId === f.id) continue;
        const li = laneIndex(r.toLaneId);
        if (li >= 0 && li < minLane) minLane = li;
      }
      const laneStart = minLane === Number.POSITIVE_INFINITY ? frame.laneSpan.start : minLane;
      const x = laneStart * LANE_WIDTH + 4;
      const cap = 6;
      let sumH = 0;
      for (let yy = yStart; yy <= yEnd; yy++) sumH += geom.rowHeights[yy] ?? ROW_MIN_H;
      if (f.kind === "do" && !f.collapsed) {
        const top = (geom.rowTops[yStart] ?? geom.headerH) + 4;
        const h = sumH - 8;
        g(overlay, { "data-subprocess-rail": f.id }, [
          { kind: "path", d: `M ${r2(x + cap)} ${r2(top)} L ${r2(x)} ${r2(top)} L ${r2(x)} ${r2(top + h)} L ${r2(x + cap)} ${r2(top + h)}`, fill: "none", stroke: t.subprocessAccent, strokeWidth: 1.5 },
        ], 0.7);
      } else if (f.kind === "each") {
        const rowTop = geom.rowTops[yStart] ?? geom.headerH;
        const top = rowTop + Math.max(rowTopPad(yStart) - 6, 4);
        const bottom = rowTop + sumH - 8;
        const h = Math.max(bottom - top, 10);
        // Committed EachRail fades only the bracket STROKE (0.85); the ↻ badge
        // text stays full opacity — no group fade. The rail is drawn BOTTOM→TOP
        // with an arrowhead at the top so it reads as the loop-back (the body
        // re-enters at the head each iteration / retry attempt), PROCESS.md §4.
        g(overlay, { "data-each-rail": f.id }, [
          { kind: "path", d: `M ${r2(x + cap)} ${r2(top + h)} L ${r2(x)} ${r2(top + h)} L ${r2(x)} ${r2(top)} L ${r2(x + cap)} ${r2(top)}`, fill: "none", stroke: t.eachRail, strokeWidth: 1.5, strokeOpacity: 0.85, dash: "4 3", marker: mark("flowTick") },
          { kind: "text", x: x + 3, y: top - 4, text: `↻ ${f.label}`, fill: t.eachRail, size: LABEL_FONT_SIZE },
        ]);
      }
    }
  }

  // ── Overlay: flow arrows + gateways + aux (flow variant) ─────────────────
  if (variant === "flow") {
    composeFlowArrows();
  }

  // ── Overlay: sequence arrows + aux arcs (re-entry + saga comp/drop) ───────
  if (variant === "sequence") {
    // Every aux back-edge draws a right-side arc so the sequence view shows the
    // same non-forward topology flow/BPMN do: a `go` re-entry (purple), the
    // reverse compensation chain (solid orange), and a forward step's failure
    // drop into that chain (dashed orange). Without the comp/drop arcs the amber
    // comp step blocks read as ordinary forward steps after the commit.
    for (const arc of swimlaneSeqArcs(input, geom, topo, opts.auxEdges ?? [])) {
      const reentry = arc.kind === "reentry";
      // Committed structure (1634-1663): group opacity only when dimmed; the
      // ARC fades to 0.95 per-shape; the label stays full + dark halo.
      const shapes: BpmnShape[] = [
        {
          kind: "path", d: arc.d, fill: "none",
          stroke: reentry ? t.reentryArc : t.compStroke, strokeWidth: reentry ? 1 : 1.25,
          opacity: 0.95, marker: mark(reentry ? "reentryTick" : "compTick"),
          ...(arc.kind === "drop" ? { dash: "4 3" } : {}),
        },
      ];
      if (reentry && arc.comment) {
        shapes.push({
          kind: "text", x: arc.commentX, y: arc.commentY, text: commentLabel(arc.comment), fill: t.reentryArc, size: ANNOTATION_FONT_SIZE, halo: t.commentHalo,
        });
      }
      const attrs = reentry
        ? { "data-seq-reentry": String(arc.fromRowIndex) }
        : { "data-seq-aux": arc.kind };
      g(overlay, attrs, shapes, arc.dimmed ? t.dimOpacity : undefined, reentry ? arc.comment : undefined);
    }
    input.rows.forEach((row, i) => {
      if (row.stepIndex === null) return;
      if (hiddenRowIndexes.has(i)) return;
      if (opts.isCommentRow?.(row, i)) return;
      if (isNote(row, i)) return;
      const flags = stepFlags(row, i);
      const fromI = laneIndex(row.fromLaneId);
      const toI = laneIndex(row.toLaneId);
      if (toI < 0) return;
      const blockX = toI * LANE_WIDTH + BLOCK_INSET;
      const blockW = LANE_WIDTH - BLOCK_INSET * 2;
      const blockCx = blockX + blockW / 2;
      const cy = bounds(row.y, toI).blockCy;
      const comp = flags.compensation === true;
      const stroke = comp ? t.compStroke : t.blockStroke;
      const dashed = comp;
      const sameLane = fromI === toI;
      const callerX = fromI >= 0 ? fromI * LANE_WIDTH + LANE_WIDTH / 2 : blockCx;
      const arrowD = sameLane
        ? seqSelfCallArc(blockX + blockW, cy)
        : `M ${r2(callerX)} ${r2(cy)} L ${r2(callerX < blockCx ? blockX : blockX + blockW)} ${r2(cy)}`;
      const dimmed = row.branchSelected === false;
      const shapes: BpmnShape[] = [];
      if (flags.dist) shapes.push({ kind: "text", x: blockX, y: cy - 12, text: "dist", fill: t.eachRail, size: ANNOTATION_FONT_SIZE, weight: 600 });
      shapes.push({
        kind: "path", d: arrowD, fill: "none", stroke, strokeWidth: 1.5,
        ...(dashed ? { dash: "6 5" } : {}), marker: mark("seq"),
      });
      if (row.stepIndex === entryStepIndex && !sameLane) {
        shapes.push({ kind: "circle", cx: callerX, cy, r: 5, fill: stroke });
      }
      g(overlay, { "data-seq-arrow": row.stepIndex, ...(comp ? { "data-compensation": "true" } : {}), ...(flags.dist ? { "data-dist": "true" } : {}) }, shapes, dimmed ? t.dimOpacity : undefined);
    });
  }

  // ── Overlay: comment labels above blocks (both variants) ─────────────────
  {
    // `go` comments already riding a DRAWN re-entry arc skip the block label.
    const onArc = new Set<number>();
    for (const e of opts.auxEdges ?? []) {
      if (e.kind !== "reentry" || !e.comment) continue;
      const from = input.rows[e.fromRowIndex];
      const to = input.rows[e.toRowIndex];
      if (!from || !to) continue;
      if (hiddenRowIndexes.has(e.fromRowIndex) || hiddenRowIndexes.has(e.toRowIndex)) continue;
      if (laneIndex(from.toLaneId) < 0 || laneIndex(to.toLaneId) < 0) continue;
      onArc.add(e.fromRowIndex);
    }
    input.rows.forEach((r, i) => {
      if (!r.comment || (r.stepIndex === null && !r.subprocessHeader)) return;
      if (hiddenRowIndexes.has(i)) return;
      if (r.reentry && onArc.has(i)) return;
      const toI = laneIndex(r.toLaneId);
      if (toI < 0) return;
      const { blockTop } = bounds(r.y, toI);
      const faded = r.branchSelected === false;
      const clearsBranchLabel = faded && !!r.branchLabel;
      g(overlay, { "data-flow-comment": r.comment }, [
        {
          kind: "text", x: toI * LANE_WIDTH + LANE_WIDTH / 2, y: blockTop - (clearsBranchLabel ? 17 : 6),
          text: commentLabel(r.comment), fill: t.blockSubtitle, size: ANNOTATION_FONT_SIZE, anchor: "middle", halo: t.commentHalo,
        },
      ], faded ? t.dimOpacity : 1, r.comment);
    });
  }

  // ── Overlay: per-hop latency labels (v0.10.2) — SEQUENCE ONLY (flow keeps
  //    just the header estimate). The callee's own latency at each call's arrow
  //    midpoint, below the arrow (mirrors the live view; export then matches). ─
  if (opts.stepLatencyLabel && input.variant === "sequence") {
    input.rows.forEach((r, i) => {
      if (r.stepIndex === null || hiddenRowIndexes.has(i)) return;
      const label = opts.stepLatencyLabel!(r.stepIndex);
      if (!label || label === "???") return; // unpriced arrows carry no label — the ??? lives in the ruler
      const toI = laneIndex(r.toLaneId);
      if (toI < 0) return;
      const fromI = laneIndex(r.fromLaneId);
      const midI = fromI >= 0 && fromI !== toI ? (fromI + toI) / 2 : toI;
      const faded = r.branchSelected === false;
      g(overlay, { "data-latency": label }, [
        {
          kind: "text", x: midI * LANE_WIDTH + LANE_WIDTH / 2, y: bounds(r.y, toI).blockCy + 13,
          text: label, fill: t.blockSubtitle, size: ANNOTATION_FONT_SIZE, anchor: "middle", halo: t.commentHalo,
        },
      ], faded ? t.dimOpacity : 1);
    });
  }

  // ── Left gutter: cumulative-elapsed ruler (v0.10.2, sequence only). Drawn at
  //    negative x; the SVG serializer extends the viewBox left by `leftGutter`.
  // Only show the ruler when the process has ≥1 actual estimation (a priced
  // step) — an all-unestimated process opts out (partial-model validity).
  const hasEstimation =
    !!opts.stepLatencyLabel &&
    input.rows.some((r) => {
      if (r.stepIndex === null) return false;
      const l = opts.stepLatencyLabel!(r.stepIndex);
      return !!l && l !== "???";
    });
  let leftGutter = 0;
  if (opts.stepCumulativeLabel && input.variant === "sequence" && hasEstimation) {
    leftGutter = RULER_GUTTER_W;
    g(overlay, { "data-ruler-header": "elapsed" }, [
      { kind: "text", x: -4, y: geom.headerH - 8, text: "elapsed", fill: t.laneHeaderText, size: ANNOTATION_FONT_SIZE, anchor: "end" },
    ]);
    input.rows.forEach((r, i) => {
      if (r.stepIndex === null || hiddenRowIndexes.has(i)) return;
      const cum = opts.stepCumulativeLabel!(r.stepIndex);
      if (!cum) return;
      g(overlay, { "data-ruler-cum": cum }, [
        { kind: "text", x: -4, y: bounds(r.y, -1).blockCy + 3, text: cum, fill: t.blockSubtitle, size: ANNOTATION_FONT_SIZE, anchor: "end" },
      ]);
    });
  }

  // Right-edge overhang: the committed overlay is `overflow: visible` (1472)
  // and labels paint past the container — a headless canvas must SIZE for
  // them (seq re-entry comments at outX+4, aux midpoint labels, mergeAspect
  // annotations). Paths stay inside the lane field + BOARD_RIGHT_PAD by
  // construction; text is the only overhang source.
  let maxTextX = 0;
  for (const grp of [...nodes, ...overlay]) {
    for (const s of grp.shapes) {
      if (s.kind !== "text") continue;
      const w = measure.text(s.text, { family: fontFamily, size: s.size, weight: s.weight ?? 400 }).width;
      const extent = s.anchor === "end" ? s.x : s.anchor === "middle" ? s.x + w / 2 : s.x + w;
      if (extent > maxTextX) maxTextX = extent;
    }
  }
  return {
    width: Math.max(geom.containerWidth, r2(maxTextX + 8)),
    height: geom.height,
    leftGutter,
    nodes,
    overlay,
    markers: used,
  };

  // ═══ Flow arrow system (committed routing + gatewayGeom + aux, ported) ════
  function composeFlowArrows(): void {
    // Routing lives in the shared scene helper (both backends consume the same
    // crossing-minimised bridge Ys).
    const { flowMap, gatewayMap } = swimlaneRouting(input, geom, topo);

    // Flow step→step arrows (+ entry stub). Arrow `d`s come from the shared
    // helper; the composer wraps each into its shape + data-flow-step group and
    // paints the entry-dot/dist markers.
    const flowEdgeGeom = swimlaneFlowEdgeGeom(input, geom, topo, flowMap);
    for (const { row, idx } of flowChain.nodes) {
      if (gatewaySkip.has(row)) continue;
      const toI = laneIndex(row.toLaneId);
      if (toI < 0) continue;
      const fromI = laneIndex(row.fromLaneId);
      const blockX = toI * LANE_WIDTH + BLOCK_INSET;
      const blockW = LANE_WIDTH - BLOCK_INSET * 2;
      const cyy = bounds(row.y, toI).blockCy;
      const flags = row.stepIndex !== null ? stepFlags(row, idx) : {};
      const comp = flags.compensation === true;
      const shapes: BpmnShape[] = [];
      if (flags.dist) shapes.push({ kind: "text", x: blockX, y: cyy - 12, text: "dist", fill: t.eachRail, size: ANNOTATION_FONT_SIZE, weight: 600 });
      const d = flowEdgeGeom.get(idx);
      if (d) {
        shapes.push({
          kind: "path", d, fill: "none",
          stroke: comp ? t.compStroke : t.arrowFlow, strokeWidth: 1,
          ...(comp ? { dash: "4 3" } : {}), marker: mark("flowTick"),
        });
      }
      const isEntry = row.stepIndex !== null && row.stepIndex === entryStepIndex;
      if (isEntry && fromI >= 0) {
        const callerCx = fromI * LANE_WIDTH + LANE_WIDTH / 2;
        const goingRight = toI >= fromI;
        const overlaps = fromI === toI || laneOccupied(row.y, fromI);
        const dotX = overlaps ? (goingRight ? blockX - 14 : blockX + blockW + 14) : callerCx;
        const targetX = dotX < blockX ? blockX : blockX + blockW;
        shapes.push({ kind: "circle", cx: dotX, cy: cyy, r: 5, fill: t.blockStroke });
        shapes.push({ kind: "path", d: `M ${r2(dotX)} ${r2(cyy)} L ${r2(targetX)} ${r2(cyy)}`, fill: "none", stroke: t.blockStroke, strokeWidth: 1.25, marker: mark("flowTick") });
      }
      if (shapes.length) {
        g(overlay, { "data-flow-step": row.stepIndex ?? undefined, ...(comp ? { "data-compensation": "true" } : {}), ...(flags.dist ? { "data-dist": "true" } : {}) }, shapes);
      }
    }

    // Gateway diamonds + leg arrows.
    for (const gg of swimlaneGatewayGeom(input, geom, topo, gatewayMap)) emitGateway(gg);

    // Aux edges (drops / comp chain / re-entries).
    composeFlowAux();
  }

  function emitGateway(gg: SwimlaneGwGeom): void {
    const { splitX, splitCy, mergeX, mergeCy, detached, mergeKind, mergeAspect } = gg;
    const shapes: BpmnShape[] = [];
    const legLabels: BpmnShape[] = [];
    for (const leg of gg.legs) {
      const stroke =
        leg.role === "head" ? (leg.isCatch ? t.arrowCatch : t.arrowHead)
        : leg.role === "tail" ? (leg.isCatch ? t.arrowCatch : t.arrowFlow)
        : t.arrowFlow;
      shapes.push({ kind: "path", d: leg.d, fill: "none", stroke, strokeWidth: 1, marker: mark(leg.isCatch ? "flowTickErr" : "flowTick") });
      if (leg.label !== undefined && leg.labelX !== undefined && leg.labelY !== undefined) {
        legLabels.push({ kind: "text", x: leg.labelX, y: leg.labelY, text: leg.label, fill: leg.isCatch ? t.arrowCatchLabel : t.blockSubtitle, size: LABEL_FONT_SIZE });
      }
    }

    // Diamond triangles (▲ split above, ▼ merge below) + annotations.
    const collapsed = gg.hiddenCount > 0;
    const triFill = collapsed ? t.gwCollapsedFill : t.gwTriangleFill;
    const triStroke = collapsed ? t.gwCollapsedStroke : t.gwTriangleStroke;
    const HW = 13;
    const tri = (dx: number, dy: number, up: boolean): BpmnShape => ({
      kind: "path",
      d: roundedPolyPath(
        up
          ? [[dx, dy - GW_HALF], [dx + HW, dy + GW_HALF], [dx - HW, dy + GW_HALF]]
          : [[dx, dy + GW_HALF], [dx - HW, dy - GW_HALF], [dx + HW, dy - GW_HALF]],
        4,
      ),
      fill: triFill, stroke: triStroke, strokeWidth: collapsed ? 2 : 1.5,
    });
    shapes.push(tri(splitX, splitCy, true));
    if (!detached) shapes.push(tri(mergeX, mergeCy, false));
    // Merge-type glyph on a PARALLEL diamond (PROCESS.md §5) so race/join/await
    // read distinctly — not just a side label. `+` = AND-join (wait all, keep
    // losers); `×` = race / deferred choice (first-wins, CANCEL losers); a clock
    // = an `await` boundary timeout. Mirrors BPMN's gateway glyph convention.
    if (mergeKind && !detached) {
      const mx = mergeX, my = mergeCy - 1, st = triStroke, sw = 1.25;
      if (mergeKind === "join") {
        shapes.push({ kind: "path", d: `M ${mx - 3} ${my} L ${mx + 3} ${my} M ${mx} ${my - 3} L ${mx} ${my + 3}`, fill: "none", stroke: st, strokeWidth: sw });
      } else if (mergeKind === "race") {
        shapes.push({ kind: "path", d: `M ${mx - 2.6} ${my - 2.6} L ${mx + 2.6} ${my + 2.6} M ${mx + 2.6} ${my - 2.6} L ${mx - 2.6} ${my + 2.6}`, fill: "none", stroke: st, strokeWidth: sw });
      } else {
        shapes.push(
          { kind: "circle", cx: mx, cy: my, r: 3.2, fill: "none", stroke: st, strokeWidth: 1 },
          { kind: "path", d: `M ${mx} ${my - 1.8} V ${my} L ${mx + 1.5} ${my + 0.9}`, fill: "none", stroke: st, strokeWidth: 1 },
        );
      }
    }
    if (mergeAspect) {
      shapes.push({
        kind: "text", x: (detached ? splitX : mergeX) + 16, y: (detached ? splitCy : mergeCy) + 3,
        text: mergeAspect, fill: t.blockSubtitle, size: LABEL_FONT_SIZE, weight: 600,
      });
    }
    if (collapsed) {
      shapes.push({ kind: "text", x: splitX + 16, y: splitCy + 3, text: `+${gg.hiddenCount}`, fill: t.gwCollapsedStroke, size: LABEL_FONT_SIZE, weight: 600 });
    }
    g(overlay, { "data-gateway-frame": gg.frameId, ...(mergeKind ? { "data-merge-kind": mergeKind } : {}) }, [...shapes, ...legLabels]);
  }

  function composeFlowAux(): void {
    // Aux routing lives in the shared helper; the composer wraps each `d` into
    // its arc shape + paints the midpoint comment.
    for (const p of swimlaneFlowAux(input, geom, topo, opts.auxEdges ?? [])) {
      const reentry = p.kind === "reentry";
      // Committed structure (1596-1628): the PATH fades to 0.95 per-shape;
      // the midpoint label stays full opacity with the dark halo.
      const shapes: BpmnShape[] = [
        {
          kind: "path", d: p.d, fill: "none",
          stroke: reentry ? t.reentryArc : t.compStroke,
          strokeWidth: reentry ? 1 : 1.25,
          opacity: 0.95,
          ...(p.kind === "drop" ? { dash: "4 3" } : {}),
          marker: mark(reentry ? "reentryTick" : "compTick"),
        },
      ];
      if (p.comment && p.midX !== undefined && p.midY !== undefined) {
        shapes.push({ kind: "text", x: p.midX, y: p.midY - 4, text: commentLabel(p.comment), fill: t.reentryArc, size: ANNOTATION_FONT_SIZE, anchor: "middle", halo: t.commentHalo });
      }
      g(overlay, { "data-aux-edge": p.kind }, shapes, undefined, p.comment);
    }
  }
}
